import {
    DetailedRoute,
    Pattern,
    Route,
    Stop,
    StopMarker,
    Time
} from '../../lib/open-trip-planner/open-trip-planner.model';
import moment from 'moment';
import { Moment } from 'moment-timezone';
import { LatLng } from '@ridango/core';
import { TranslateService } from '@ngx-translate/core';
import { Temporal } from '@js-temporal/polyfill';

const HexBaseRegex = /^([0-9A-F]{3}){1,2}$/i;

export class OtpUtil {
    static resolveRouteColor(route: Route | DetailedRoute): string {
        return OtpUtil.resolveColor(route?.icon?.color || route?.color);
    }

    static resolveColor(hexBase: string, fallback?: string): string {
        if (hexBase && hexBase.includes('#')) {
            return hexBase;
        }
        return HexBaseRegex.test(hexBase) ? `#${hexBase}` : fallback ?? 'var(--color-brand-primary)';
    }

    static getScheduledDepartureTime(time: Time): number {
        if (typeof time.scheduledDeparture === 'string') {
            return (new Date(time.scheduledDeparture).valueOf() / 1000) * 1000;
        } else {
            return (time.serviceDay + time.scheduledDeparture) * 1000;
        }
    }

    static getScheduledDepartureMoment(time: Time): Moment {
        return moment(OtpUtil.getScheduledDepartureTime(time));
    }

    static unixToDate(unixTimestamp: number, zone: string): Temporal.ZonedDateTime {
        return Temporal.Instant.fromEpochMilliseconds(unixTimestamp).toZonedDateTimeISO(zone);
    }

    static isMobileDevice(): boolean {
        return window.innerWidth < 768;
    }

    static transformStopsStopMarkers(stops: Stop[]): StopMarker[] {
        return (
            stops.map((stop) => ({
                name: stop.name,
                code: stop.code,
                dist: stop.dist,
                latlng: { lat: stop.lat, lng: stop.lon } as LatLng
            })) ?? []
        );
    }

    static getPatternDisplayLabel(pattern: Pattern | null | undefined, preferTripHeadsign: boolean): string {
        if (!pattern) {
            return '';
        }
        if (preferTripHeadsign) {
            const h = pattern.tripHeadsign?.trim();
            if (h) {
                return h;
            }
            return pattern.desc ?? '';
        }
        return pattern.desc ?? '';
    }

    static getPatternDescription(pattern: Pattern, stops: Stop[]) {
        if (pattern && stops.length) {
            let desc = stops[0].name;
            const via = this.getPatternDescriptionViaStopName(pattern);
            if (via) {
                desc += ` - ${via}`;
            }
            return desc + ` - ${stops[stops.length - 1].name}`;
        }
        return '';
    }

    private static getPatternDescriptionViaStopName(pattern: Pattern): string | undefined {
        const description = pattern.desc;
        const startStr = 'via ';
        const endStr = '(';
        const startPos = description.indexOf(startStr);
        const endPos = description.indexOf(endStr, startPos);
        if (startPos < 0 || endPos < 0) {
            return undefined;
        }
        return description.substring(startPos + startStr.length, endPos);
    }

    static shadeHexColor(color: string, percent: number): string | null {
        if (!color) {
            return null;
        }
        color = OtpUtil.resolveColor(color);
        const f = parseInt(color.slice(1), 16),
            t = percent < 0 ? 0 : 255,
            p = percent < 0 ? percent * -1 : percent,
            R = f >> 16,
            G = (f >> 8) & 0x00ff,
            B = f & 0x0000ff;
        return (
            '#' +
            (
                0x1000000 +
                (Math.round((t - R) * p) + R) * 0x10000 +
                (Math.round((t - G) * p) + G) * 0x100 +
                (Math.round((t - B) * p) + B)
            )
                .toString(16)
                .slice(1)
        );
    }

    static isDateInPast(dateValue: number | string) {
        const currentTime = Temporal.Now.instant().toZonedDateTimeISO('UTC').round('minute');

        const scheduledDepartureTime =
            typeof dateValue === 'string'
                ? Temporal.Instant.from(dateValue).toZonedDateTimeISO('UTC')
                : Temporal.Instant.fromEpochSeconds(dateValue).toZonedDateTimeISO('UTC');

        const minutesUntilDeparture = currentTime
            .until(scheduledDepartureTime.round('minute'))
            .total({ unit: 'minute' });
        return minutesUntilDeparture < 0;
    }

    static secondsToClockString(
        dateValue: number | string,
        translate: TranslateService = null,
        realtimeThreshold: number = null,
        timezone: string = null
    ): string {
        let scheduledDepartureTime;

        if (typeof dateValue === 'string') {
            scheduledDepartureTime = moment.utc(dateValue);
        } else {
            scheduledDepartureTime = moment.utc(dateValue * 1000);
        }

        if (realtimeThreshold) {
            const currentTime = moment().utc();
            const isToday = currentTime.day() === scheduledDepartureTime.day();
            const currentMinutesTotal = currentTime.hours() * 60 + currentTime.minutes();
            const departureMinutesTotal = scheduledDepartureTime.hours() * 60 + scheduledDepartureTime.minutes();
            const minutesUntilDeparture = departureMinutesTotal - currentMinutesTotal;

            if (minutesUntilDeparture === 0) {
                return translate.instant('EXPLORE_NOW_LABEL');
            }

            if (minutesUntilDeparture < realtimeThreshold && minutesUntilDeparture >= 0 && isToday) {
                return `${minutesUntilDeparture} ${translate ? translate.instant('EXPLORER_SCHEDULE_MINUTE') : 'min'}`;
            }
        }

        if (timezone) {
            return scheduledDepartureTime.tz(timezone).format('HH:mm');
        }

        return scheduledDepartureTime.format('HH:mm');
    }
}
